import * as React from 'react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import type { FormatTypes } from 'modules/core/lib/dates'
import { stringToDate } from 'modules/core/lib/dates'
import DeleteBulkModal from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/DeleteBulkModal'
import DeleteSelectedModal from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/DeleteSelectedModal'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import shiftManagementCounts from 'modules/shift_management/lib/shiftManagementCounts'
import type { RestDay } from 'modules/shift_management/lib/types'
import type Shift from 'modules/shift_management/store/internal/models/Shift'

type DeleteActionSource = 'top_bar_button' | 'keyboard'

interface UseDeleteSelectionFlowParams {
  disabled?: boolean
}

const isContiguousDateSelection = (
  daysByRow: Record<string, string[]>,
  dateFormat: FormatTypes
) => {
  const selectedDates = new Set(Object.values(daysByRow).flat())
  const sortedDates = Array.from(selectedDates)
    .map((date) => stringToDate(date, dateFormat))
    .sort((left, right) => left.diff(right))

  return sortedDates.every((date, index) => {
    const previousDate = sortedDates[index - 1]

    return !previousDate || date.diff(previousDate, 'days') === 1
  })
}

const useDeleteSelectionFlow = ({ disabled }: UseDeleteSelectionFlowParams) => {
  const [{ view }] = useSchedule()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasNewTableAbstractionFeatureFlag = hasFeatureFlag(
    'dev_shift_management_shifts_table_abstraction'
  )
  const {
    shifts: selectedShifts,
    restDays: selectedRestDays,
    daysByRow,
    kind,
    isEnabled,
    isResolvingGridSelection,
  } = useSelectionState()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false)
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = React.useState(false)

  const shifts = useUnsafeMemo(() => Object.values(selectedShifts), [selectedShifts])
  const restDays = useUnsafeMemo(() => Object.values(selectedRestDays), [selectedRestDays])
  const isDeleteActionDisabled = Boolean(disabled) || isResolvingGridSelection
  const isAnyDeleteModalOpen = isDeleteModalOpen || isBulkDeleteModalOpen

  const openDeleteFlow = useUnsafeCallback(
    (source: DeleteActionSource = 'top_bar_button') => {
      if (isDeleteActionDisabled || isAnyDeleteModalOpen) return

      const { shiftsCount, restDaysCount, employeeCount } = shiftManagementCounts(shifts, restDays)
      const shiftsIds = shifts.map((shift: Shift) => shift.id)
      const restDayIds = restDays.map((restDay: RestDay) => restDay.id)
      const selectedEmployeeIds = Object.keys(daysByRow)

      track(trackingEvents.shiftManagement.deleteShiftStart, {
        shifts_ids: shiftsIds,
        rest_day_ids: restDayIds,
        number_of_impacted_employees: Math.max(employeeCount, selectedEmployeeIds.length),
        number_of_impacted_shifts: shiftsCount,
        number_of_impacted_rest_days: restDaysCount,
        view: view.viewType,
        view_type_by: view.groupType,
        from: source,
      })

      if (!hasNewTableAbstractionFeatureFlag) {
        if (employeeCount === 0) {
          setIsBulkDeleteModalOpen(true)
          return
        }

        setIsDeleteModalOpen(true)
        return
      }

      if (kind === 'cells' || kind === 'event') {
        setIsDeleteModalOpen(true)
        return
      }

      if (kind === 'columnRange' && !isContiguousDateSelection(daysByRow, view.dateFormat)) {
        setIsDeleteModalOpen(true)
        return
      }

      setIsBulkDeleteModalOpen(true)
    },
    [
      daysByRow,
      hasNewTableAbstractionFeatureFlag,
      isAnyDeleteModalOpen,
      isDeleteActionDisabled,
      kind,
      restDays,
      shifts,
      view.groupType,
      view.viewType,
    ]
  )

  const handleDeleteShortcut = useUnsafeCallback(
    (event: KeyboardEvent) => {
      if (isDeleteActionDisabled || !isEnabled) return
      if (event.key !== 'Backspace' && event.key !== 'Delete') return
      if (event.repeat) return
      openDeleteFlow('keyboard')
    },
    [isDeleteActionDisabled, isEnabled, openDeleteFlow]
  )

  const deleteModal = useUnsafeMemo(() => {
    return (
      <>
        {isBulkDeleteModalOpen && (
          <DeleteBulkModal onClose={() => setIsBulkDeleteModalOpen(false)} />
        )}
        {isDeleteModalOpen && (
          <DeleteSelectedModal
            shifts={shifts}
            restDays={restDays}
            onClose={() => setIsDeleteModalOpen(false)}
          />
        )}
      </>
    )
  }, [isBulkDeleteModalOpen, isDeleteModalOpen, restDays, shifts])

  return {
    deleteModal,
    handleDeleteShortcut,
    isDeleteActionDisabled,
    openDeleteFlow,
  }
}

export { useDeleteSelectionFlow }
